import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { FormattedMessage, useIntl } from "react-intl";
import { useAuthStore } from "@/lib/stores/authStore";
import { usePathModeStore } from "@/lib/stores/pathModeStore";
import { useTrainerAuthRedirect } from "@/lib/hooks/useTrainerAuthRedirect";
import { showToast } from "@/lib/utils/toast";
import loginImage from "@/assets/exercise-background-photos/login-page-3.png";
import { AuthFormCard } from "@/components/AuthFormCard";
import { AuthHeader } from "@/components/AuthHeader";
import { AuthBackButton } from "@/components/AuthBackButton";
import { AuthErrorAlert } from "@/components/AuthErrorAlert";
import { AuthTextInput } from "@/components/AuthTextInput";
import { AuthPasswordInput } from "@/components/AuthPasswordInput";
import { AuthFooterLink } from "@/components/AuthFooterLink";
import { Link } from "@tanstack/react-router";

export const Route = createFileRoute("/trainer/auth/login")({
  component: TrainerLogin,
});

function TrainerLogin() {
  const navigate = useNavigate();
  const intl = useIntl();

  const { user, login, isLoading, error, clearError } = useAuthStore();
  const setMode = usePathModeStore((s) => s.setMode);
  const { goAfterAuth } = useTrainerAuthRedirect();

  const loginSchema = z.object({
    nickname: z
      .string()
      .min(1, intl.formatMessage({ id: "validation.nickname.required" })),
    password: z
      .string()
      .min(1, intl.formatMessage({ id: "validation.password.required" })),
  });

  type LoginFormData = z.infer<typeof loginSchema>;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({ resolver: zodResolver(loginSchema) });

  useEffect(() => {
    clearError();
  }, [clearError]);

  useEffect(() => {
    if (user?.accountType === "TRAINER") {
      goAfterAuth();
    }
  }, [user, goAfterAuth]);

  const onSubmit = async (data: LoginFormData) => {
    try {
      clearError();
      await login(data);
      setMode("TRAINER");
      showToast.success(
        intl.formatMessage(
          { id: "toast.auth.loginSuccess" },
          { username: data.nickname },
        ),
      );
      goAfterAuth();
    } catch {
      // Error handling is managed in the store
    }
  };

  return (
    <div className="flex flex-col relative">
      <AuthHeader
        image={loginImage}
        imageAlt="Sign In"
        title={<FormattedMessage id="login.title" />}
        subtitle={<FormattedMessage id="login.dataProtected" />}
      />

      <AuthFormCard>
        <div className="flex flex-col">
          <AuthBackButton onClick={() => navigate({ to: "/trainer/auth" })} />

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {error && <AuthErrorAlert>{error}</AuthErrorAlert>}

            <AuthTextInput
              {...register("nickname")}
              placeholder={intl.formatMessage({
                id: "login.nickname.placeholder",
              })}
              disabled={isLoading}
              onFocus={clearError}
              error={errors.nickname?.message}
            />

            <AuthPasswordInput
              {...register("password")}
              placeholder={intl.formatMessage({
                id: "login.password.placeholder",
              })}
              disabled={isLoading}
              onFocus={clearError}
              error={errors.password?.message}
            />

            <div className="text-right rtl:text-left">
              <Link
                to="/auth/forgot-password"
                className="text-small text-dark"
              >
                <FormattedMessage id="login.forgotPassword" />
              </Link>
            </div>

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? (
                <FormattedMessage id="login.loggingIn" />
              ) : (
                <FormattedMessage id="login.login" />
              )}
            </Button>

            <AuthFooterLink
              promptId="login.noAccount"
              linkId="login.signUp"
              to="/trainer/auth/register"
            />
          </form>
        </div>
      </AuthFormCard>
    </div>
  );
}
